import { unref } from 'vue'
import { result as _result } from 'lodash-es'
import { i18n } from '@/i18n'

/**
 * Подпись dropdown с учётом текущей (или переданной) локали.
 * EN → value_en (fallback value); RU → value.
 *
 * @param {object|null|undefined} item dropdown / short dict
 * @param {string|import('vue').Ref} [locale]
 * @returns {string}
 */
export function dropdownLabel(item, locale = i18n.global.locale.value) {
  if (item == null) return ''
  if (typeof item !== 'object') return String(item)

  const loc = String(unref(locale) || 'ru').toLowerCase()
  if (loc.startsWith('en')) {
    const en = String(item.value_en ?? '').trim()
    if (en) return en
  }

  const v = item.value ?? item.label ?? item.name
  return v == null ? '' : String(v)
}

/**
 * Подпись сущности с name / name_eng (ActivityTypes и др.).
 * EN → name_eng (fallback name); RU → name.
 *
 * @param {object|null|undefined} item
 * @param {string|import('vue').Ref} [locale]
 * @returns {string}
 */
export function namedEntityLabel(item, locale = i18n.global.locale.value) {
  if (item == null) return ''
  if (typeof item !== 'object') return String(item)

  const loc = String(unref(locale) || 'ru').toLowerCase()
  const name = String(item.name ?? '').trim()
  const nameEng = String(item.name_eng ?? '').trim()

  if (loc.startsWith('en')) {
    return nameEng || name
  }

  return name || nameEng
}

/**
 * Подпись parent_name / parent_name_eng у ActivityTypes short.
 *
 * @param {object|null|undefined} item activity type / subtype short
 * @param {string|import('vue').Ref} [locale]
 * @returns {string}
 */
export function activityParentLabel(item, locale = i18n.global.locale.value) {
  if (item == null) return ''
  if (typeof item !== 'object') return String(item)

  const loc = String(unref(locale) || 'ru').toLowerCase()
  if (loc.startsWith('en')) {
    const en = String(item.parent_name_eng ?? '').trim()
    if (en) return en
  }

  const v = item.parent_name
  return v == null ? '' : String(v)
}

/**
 * Значение ячейки списка по path: для `*.value` учитывает value_en;
 * для `.name` — name_eng; для `.parent_name` — parent_name_eng.
 * Колонка с путём `name_eng` не переписывается: это отдельное английское поле.
 *
 * @param {object} row
 * @param {string|null|undefined} path
 * @param {*} [fallback]
 * @param {string} [locale]
 * @returns {*}
 */
export function pathLabel(row, path, fallback = undefined, locale = i18n.global.locale.value) {
  if (!path) {
    return fallback
  }
  const loc = unref(locale)
  const pathStr = String(path)

  if (pathStr.endsWith('.value')) {
    const parentPath = pathStr.slice(0, -'.value'.length)
    const parent = _result(row, parentPath)
    if (parent && typeof parent === 'object' && ('value' in parent || 'value_en' in parent || 'key' in parent)) {
      const label = dropdownLabel(parent, loc)
      return label !== '' ? label : fallback
    }
  }

  if (pathStr.endsWith('.parent_name')) {
    const parentPath = pathStr.slice(0, -'.parent_name'.length)
    const parent = _result(row, parentPath)
    if (parent && typeof parent === 'object' && ('parent_name' in parent || 'parent_name_eng' in parent)) {
      const label = activityParentLabel(parent, loc)
      return label !== '' ? label : fallback
    }
  }

  if (pathStr === 'name' || pathStr.endsWith('.name')) {
    const parent = pathStr === 'name' ? row : _result(row, pathStr.slice(0, -'.name'.length))
    if (parent && typeof parent === 'object' && ('name' in parent || 'name_eng' in parent)) {
      const label = namedEntityLabel(parent, loc)
      return label !== '' ? label : fallback
    }
  }

  const v = _result(row, path)
  return v !== undefined && v !== null ? v : fallback
}

/**
 * Ячейка списка справочника. Колонка `name` — на языке интерфейса.
 * Колонка `name_eng` остаётся английским полем.
 *
 * @param {object} row
 * @param {{ title?: string, path?: string }} col
 * @param {string|import('vue').Ref} [locale]
 * @returns {*}
 */
export function listCell(row, col, locale = i18n.global.locale.value) {
  const raw = _result(row, col?.path, row?.[col?.title])
  const path = col?.path || (col?.title === 'name' ? 'name' : '')
  if (!path) return raw
  return pathLabel(row, path, raw, locale)
}
